'use client'

import React, { useCallback, useMemo } from 'react'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Category } from '@/payload-types'
import { cn } from '@/utilities/cn'

type DiscountSummary = {
  id: number
  name: string
}

interface AppliedFiltersProps {
  categories: Category[]
  discounts?: DiscountSummary[]
}

export const AppliedFilters: React.FC<AppliedFiltersProps> = ({ categories, discounts = [] }) => {
  const t = useTranslations('filters')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const activeCategoryId = useMemo(() => {
    const categoryParam = searchParams.get('category')
    return categoryParam ? parseInt(categoryParam) : null
  }, [searchParams])

  const activeCategory = useMemo(() => {
    return categories.find((c) => c.id === activeCategoryId)
  }, [categories, activeCategoryId])

  const activeDiscountId = searchParams.get('discount')
  const activeDiscount = useMemo(() => {
    if (!activeDiscountId) return null
    const id = parseInt(activeDiscountId, 10)
    if (Number.isNaN(id)) return null
    return discounts.find((discount) => discount.id === id) ?? null
  }, [activeDiscountId, discounts])

  // Build breadcrumb path for active category
  const categoryPath = useMemo(() => {
    if (!activeCategory) return []

    const path: Category[] = [activeCategory]
    let current = activeCategory

    while (current.parent) {
      const parentId = typeof current.parent === 'object' ? current.parent.id : current.parent
      const parent = categories.find((c) => c.id === parentId)
      if (parent) {
        path.unshift(parent)
        current = parent
      } else {
        break
      }
    }

    return path
  }, [activeCategory, categories])

  const handleRemoveFilter = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('category')
    const newParams = params.toString()
    router.push(pathname + (newParams ? '?' + newParams : ''))
  }, [pathname, router, searchParams])

  const handleRemoveDiscount = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('discount')
    const newParams = params.toString()
    router.push(pathname + (newParams ? '?' + newParams : ''))
  }, [pathname, router, searchParams])

  return (
    <div className="flex flex-col gap-2 mb-4 overflow-visible" data-testid="applied-filters">
      <span className="text-xs text-white/90 font-mono font-bold uppercase tracking-[0.2em] pb-2 border-b border-white/10">
        {t('filteredBy')}
      </span>
      <div className="flex flex-wrap gap-2 min-h-[36px] pt-2 overflow-visible">
        {activeCategory && (
          <AnimatePresence>
            <motion.button
              onClick={handleRemoveFilter}
              className={cn(
                'flex items-center gap-2',
                'font-mono text-xs',
                'px-3 py-1.5 rounded-md',
                'bg-black/30 border border-white/8',
                'text-neon-orange',
                'hover:bg-white/10 hover:border-[var(--neon-orange)]/60',
                'hover:shadow-[0_0_20px_-5px_var(--neon-orange)]',
                'transition-all duration-200',
                'group',
                'w-fit',
                'shadow-[0_0_15px_-3px_var(--neon-orange)]',
              )}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label={`Remove ${activeCategory.title} filter`}
            >
              <span className="flex items-center gap-1">
                {categoryPath.map((cat, index) => (
                  <React.Fragment key={cat.id}>
                    {index > 0 && <span className="text-neutral-400 dark:text-neutral-500 font-medium">/</span>}
                    <span
                      className={cn(
                        'font-medium text-xs',
                        index === categoryPath.length - 1
                          ? 'uppercase text-purple-400'
                          : 'text-white/70'
                      )}
                    >
                      {cat.title}
                    </span>
                  </React.Fragment>
                ))}
              </span>
              <X
                className="h-4 w-4 p-[2px] rounded-sm text-red-600 group-hover:text-red-700 group-hover:bg-red-500/10 group-hover:rotate-90 transition-all duration-200"
                aria-hidden="true"
                data-testid="x-icon"
              />
            </motion.button>
          </AnimatePresence>
        )}

        {activeDiscount && (
          <AnimatePresence>
            <motion.button
              onClick={handleRemoveDiscount}
              className={cn(
                'flex items-center gap-2',
                'font-mono text-xs',
                'px-3 py-1.5 rounded-md',
                'bg-black/30 border border-white/8',
                'text-neon-orange',
                'hover:bg-white/10 hover:border-[var(--neon-orange)]/60',
                'hover:shadow-[0_0_20px_-5px_var(--neon-orange)]',
                'transition-all duration-200',
                'group',
                'w-fit',
                'shadow-[0_0_15px_-3px_var(--neon-orange)]',
              )}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label={t('clearDiscount', { name: activeDiscount.name })}
            >
              <span className="flex items-center gap-1">
                <span className="uppercase">{activeDiscount.name}</span>
              </span>
              <X
                className="h-4 w-4 p-[2px] rounded-sm text-red-600 group-hover:text-red-700 group-hover:bg-red-500/10 group-hover:rotate-90 transition-all duration-200"
                aria-hidden="true"
                data-testid="x-icon"
              />
            </motion.button>
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}
